import { zodResolver } from '@hookform/resolvers/zod';

import { Add } from '@mui/icons-material';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';

import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';

import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { z } from 'zod';

import CountryOptionLabel from '../../../../../shared/components/mbui/CountryOptionLabel';

import IDOptionLabel, {
  getIDOptionLabel,
} from '../../../../../shared/components/mbui/IDOptionLabel';

import RHFAutocomplete from '../../../../../shared/components/react-hook-form/RHFAutocomplete';

import RHFDatePicker from '../../../../../shared/components/react-hook-form/RHFDatePicker';

import RHFForm from '../../../../../shared/components/react-hook-form/RHFForm';

import RHFTextField from '../../../../../shared/components/react-hook-form/RHFTextField';

import { useTheme } from '@mui/material/styles';

import useFetchData from '../../../hooks/useFetchData';

import { getPortsByCountry } from '../../../../../features/master-data';

import {
  getScenarioHighlightColor,
  scenarioFieldHighlightSx,
} from '../../../../../shared/utils/scenarioHighlight';

import useUserRoles from '../../../../../shared/hooks/useUserRoles';

import DestinationBlock from './DestinationBlock';

interface Country {
  name: string;

  countryCode: string;
}

interface Supplier {
  supplierName: string;

  supplierNo: string | number;
}

interface Ship {
  imo: string;

  name?: string;

  supplier?: { supplierNo: string | number };
}

interface Port {
  unLocode: string;

  name: string;
}

dayjs.extend(utc);

const shipmentSchema = z.object({
  id: z.string().or(z.number()).optional(),

  _shipmentId: z.string().optional(),

  _scenarioCreated: z.boolean().optional(),

  market: z

    .string()

    .or(z.number())

    .nullable()

    .refine((v) => v !== null, {
      message: 'Market is required',
    }),

  firstShippingLineOffer: z

    .number()

    .min(0, { message: '0 (Min)' })

    .max(4000, { message: '4000 (Max)' }),

  adjustedPORequest: z

    .number()

    .min(0, { message: '0 (Min)' })

    .max(4000, { message: '4000 (Max)' }),

  finalAgreement: z

    .number()

    .min(0, { message: '0 (Min)' })

    .max(4000, { message: '4000 (Max)' }),

  effectiveShipped: z

    .number()

    .min(0, { message: '0 (Min)' })

    .max(4000, { message: '4000 (Max)' }),
});

const destinationSchema = z.object({
  _voyageId: z.string().optional(),

  _voyageNumber: z.string().optional(),

  _deltaId: z.string().optional(),

  _cancelled: z.boolean().optional(),

  _scenarioCreated: z.boolean().optional(),

  destinationCountry: z.string().nullable(),

  destinationPort: z.string().nullable(),

  initialETA: z.any().nullable().optional(),

  actualETA: z.any().nullable().optional(),

  generalRemarks: z.string().optional(),

  shipments: z.array(shipmentSchema),
});

/**



 * Cross-field refinement: only enforce required fields & min-shipments



 * on destinations that are NOT marked as cancelled.



 */

const activeDestinationRefinement = (
  data: { destinations: Array<Record<string, any>> },

  ctx: z.RefinementCtx
) => {
  data.destinations.forEach((dest, i) => {
    if (dest._cancelled) return; // skip validation for cancelled destinations

    if (
      !dest.destinationCountry ||
      String(dest.destinationCountry).length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,

        message: 'Destination Country is required',

        path: ['destinations', i, 'destinationCountry'],
      });
    }

    if (!dest.destinationPort || String(dest.destinationPort).length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,

        message: 'Destination Port is required',

        path: ['destinations', i, 'destinationPort'],
      });
    }

    if (dest.actualETA == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,

        message: 'Latest ETA is required',

        path: ['destinations', i, 'actualETA'],
      });
    }

    if (!dest.shipments || dest.shipments.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,

        message: 'At least one shipment is required',

        path: ['destinations', i, 'shipments'],
      });
    }

    // Duplicate market ID check within this destination

    if (dest.shipments && dest.shipments.length > 1) {
      const seenMarkets = new Set<string>();

      dest.shipments.forEach((sh: Record<string, any>, j: number) => {
        const mkt = sh.market;

        if (mkt !== null && mkt !== undefined && String(mkt).length > 0) {
          if (seenMarkets.has(String(mkt))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,

              message: 'Duplicate market',

              path: ['destinations', i, 'shipments', j, 'market'],
            });
          }

          seenMarkets.add(String(mkt));
        }
      });
    }
  });

  // Multiple voyages to the same destination port are valid — the backend
  // identifies voyages by UUID, not by port. No duplicate port check needed.
};

const formSchema = z

  .object({
    imo: z

      .string()

      .nullable()

      .refine((v) => v !== null && String(v).length > 0, {
        message: 'IMO Number is required',
      }),

    vessel: z.string().optional(),

    voyageNumber: z

      .string()

      .optional()

      .transform((v) => (v ? v.toUpperCase() : v)),

    carrier: z.string().or(z.number()).nullable().optional(),

    shippingCompany: z.string().or(z.number()).nullable().optional(),

    departureCountry: z

      .string()

      .nullable()

      .refine((v) => v !== null && String(v).length > 0, {
        message: 'Departure Country is required',
      }),

    departurePort: z

      .string()

      .nullable()

      .refine((v) => v !== null && String(v).length > 0, {
        message: 'Departure Port is required',
      }),

    portOperator: z

      .string()

      .or(z.number())

      .nullable()

      .refine((v) => v !== null, {
        message: 'Port Operator is required',
      }),

    slotConfirmed: z.boolean(),

    initialETD: z.any().nullable().optional(),

    actualETD: z.any().nullable().optional(),

    destinations: z

      .array(destinationSchema)

      .min(1, { message: 'At least one destination is required' }),
  })

  .superRefine((data, ctx) => {
    const toMs = (v: unknown): number | null => {
      if (v == null) return null;

      const d = dayjs.utc(v as dayjs.ConfigType);

      return d.isValid() ? d.valueOf() : null;
    };

    const actualEtdMs = toMs(data.actualETD);

    // Per-destination ETA vs ETD validation

    if (data.destinations && actualEtdMs !== null) {
      data.destinations.forEach((dest, i) => {
        const destEtaMs = toMs(dest.actualETA);

        if (destEtaMs !== null && destEtaMs < actualEtdMs) {
          ctx.addIssue({
            code: 'custom',

            path: ['destinations', i, 'actualETA'],

            message: 'Latest ETA cannot be earlier than Latest ETD',
          });
        }
      });
    }

    // Validate active (non-cancelled) destinations

    activeDestinationRefinement(data as any, ctx);
  });

export type VesselFormValues = z.infer<typeof formSchema>;

export const defaultDestination = {
  destinationCountry: '',

  destinationPort: '',

  initialETA: null,

  actualETA: null,

  generalRemarks: '',

  shipments: [
    {
      id: undefined,

      market: null,

      firstShippingLineOffer: 0,

      adjustedPORequest: 0,

      finalAgreement: 0,

      effectiveShipped: 0,
    },
  ],
};

const defaultValues = {
  imo: '',

  vessel: '',

  voyageNumber: '',

  carrier: null,

  shippingCompany: null,

  departureCountry: '',

  departurePort: '',

  portOperator: null,

  slotConfirmed: false,

  initialETD: null,

  actualETD: dayjs(),

  destinations: [defaultDestination],
} satisfies Record<string, unknown>;

const RHFVesselForm = forwardRef<
  HTMLFormElement,
  {
    onSubmit: (data: VesselFormValues) => void;

    editData?: Partial<VesselFormValues> | undefined;

    mode?: 'create' | 'edit' | 'group-edit';

    prefetchedDeparturePorts?: { label: string; value: string }[];

    voyageChangedFieldsMap?: Record<string, Set<string>>;

    shipmentChangedFieldsMap?: Record<string, Set<string>>;

    topLevelChangedFields?: Set<string>;

    scenarioCancelledVoyageIds?: Set<string>;

    readOnly?: boolean;
  }
>(function RHFVesselForm(
  {
    onSubmit,

    editData,

    mode = 'create',

    prefetchedDeparturePorts,

    voyageChangedFieldsMap,

    shipmentChangedFieldsMap,

    topLevelChangedFields,

    scenarioCancelledVoyageIds,

    readOnly = false,
  },

  ref
) {
  const {
    countries,

    shippingCompanies,

    ships,

    portOperator: portOperators,

    markets,

    isLoaded: isRefDataLoaded,
  } = useFetchData() as {
    countries: Country[];

    shippingCompanies: Supplier[];

    ships: Ship[];

    portOperator: Supplier[];

    markets: { id: string | number; name: string }[];

    isLoaded: boolean;
  };

  const { isPortOperatorUser, supplier } = useUserRoles();

  const theme = useTheme();

  const methods = useForm<VesselFormValues>({
    resolver: zodResolver(formSchema) as any,

    defaultValues: defaultValues as any,

    mode: 'onSubmit',

    reValidateMode: 'onChange',
  });

  const {
    control,

    setValue,

    getValues,

    reset,

    watch,

    formState: { errors },
  } = methods;

  const {
    fields: destinationFields,

    append: appendDestination,

    remove: removeDestination,
  } = useFieldArray({
    control,

    name: 'destinations',
  });

  const [departurePorts, setDeparturePorts] = useState<
    { label: string; value: string }[]
  >([]);

  const [isReady, setIsReady] = useState(!editData);

  const [isLoadingPorts, setIsLoadingPorts] = useState(false);

  const countryOptions = useMemo(
    () => countries.map((v) => ({ label: v.name, value: v.countryCode })),

    [countries]
  );

  const shippingCompanyOptions = useMemo(
    () =>
      shippingCompanies.map((v) => ({
        label: v.supplierName,

        value: v.supplierNo,
      })),

    [shippingCompanies]
  );

  const portOperatorOptions = useMemo(
    () =>
      portOperators.map((v) => ({
        label: v.supplierName,

        value: v.supplierNo,
      })),

    [portOperators]
  );

  const imoOptions = useMemo(
    () =>
      ships.map((v) => ({
        label: v.name ? `${v.imo} - ${v.name}` : String(v.imo),

        value: v.imo,

        vesselName: v.name,

        supplierNo: v.supplier?.supplierNo,
      })),

    [ships]
  );

  const imoSupplierNoMapping = useMemo(() => {
    return ships.reduce<Record<string, { supplierNo: string | number | null }>>(
      (pV, cV) => {
        pV[cV.imo] = { supplierNo: cV.supplier?.supplierNo ?? null };

        return pV;
      },

      {}
    );
  }, [ships]);

  const marketOptions = useMemo(
    () =>
      (markets || [])

        .map((m: any) => ({ label: m.name, value: m.id }))

        .sort((a: any, b: any) =>
          String(a.label).localeCompare(String(b.label), undefined, {
            sensitivity: 'base',
          })
        ),

    [markets]
  );

  useEffect(() => {
    let cancelled = false;

    const loadEditData = async () => {
      if (editData && isRefDataLoaded) {
        // If departure ports are pre-fetched, skip the async call

        if (prefetchedDeparturePorts) {
          setDeparturePorts(prefetchedDeparturePorts);
        } else {
          setIsReady(false);

          const dep = editData?.departureCountry as string | undefined;

          if (dep) {
            try {
              const ports: Port[] = await getPortsByCountry(dep);

              if (!cancelled) {
                setDeparturePorts(
                  ports.map((v) => ({
                    label: `${v.unLocode} ${v.name}`,

                    value: v.unLocode,
                  }))
                );
              }
            } catch {
              if (!cancelled) setDeparturePorts([]);
            }
          }
        }

        if (!cancelled) {
          const merged = {
            ...defaultValues,

            ...editData,

            slotConfirmed:
              typeof editData?.slotConfirmed === 'boolean'
                ? editData.slotConfirmed
                : false,
          } as VesselFormValues;

          reset(merged);

          setIsReady(true);
        }
      }
    };

    loadEditData();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, isRefDataLoaded]);

  // Auto-fill shipping company and vessel name when IMO changes (create mode only)

  const watchImo = watch('imo');

  useEffect(() => {
    if (mode !== 'create') return;

    const imo = watchImo as string | null;

    const supplierNo = imo
      ? imoSupplierNoMapping[String(imo)]?.supplierNo
      : null;

    setValue('shippingCompany', (supplierNo ?? null) as any);

    const vesselName =
      imoOptions.find((op) => op.value === imo)?.vesselName || '';

    setValue('vessel', vesselName);
  }, [watchImo, setValue, imoSupplierNoMapping, imoOptions, mode]);

  // Watch country selections and fetch ports using primitive codes

  const watchDepartureCountry = watch('departureCountry');

  const initialDepartureCountryRef = useRef<string | null | undefined>(
    undefined
  );

  useEffect(() => {
    const code = watchDepartureCountry as string | null;

    // On first render, record the initial country and use the pre-fetched ports.
    if (initialDepartureCountryRef.current === undefined) {
      initialDepartureCountryRef.current = code;
      if (prefetchedDeparturePorts && departurePorts.length > 0) return;
    }

    // If the country hasn't changed from the initial value, pre-fetched ports are valid.
    if (
      prefetchedDeparturePorts &&
      code === initialDepartureCountryRef.current &&
      departurePorts.length > 0
    )
      return;

    let cancelled = false;

    const fetchPorts = async () => {
      if (!code) {
        setDeparturePorts([]);

        setValue('departurePort', null as any);

        return;
      }

      setIsLoadingPorts(true);

      try {
        const portsData = await getPortsByCountry(code);

        if (cancelled) return;

        const options = portsData.map((v: any) => ({
          label: `${v.unLocode} ${v.name}`,

          value: v.unLocode,
        }));

        setDeparturePorts(options);

        // Only clear the port if the current value is not in the fetched list

        const currentPort = getValues('departurePort');

        if (currentPort && !options.some((o: any) => o.value === currentPort)) {
          setValue('departurePort', null as any);
        }
      } catch (e) {
        if (cancelled) return;

        setDeparturePorts([]);

        setValue('departurePort', null as any);
      } finally {
        if (!cancelled) setIsLoadingPorts(false);
      }
    };

    fetchPorts();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchDepartureCountry, setValue, getValues, prefetchedDeparturePorts]);

  // If user is a Port Operator, preselect the Port Operator using options list (create mode only)

  useEffect(() => {
    if (mode !== 'create') return;

    if (!isPortOperatorUser) return;

    if (supplier == null || supplier === '') return;

    const normalized = isNaN(Number(supplier))
      ? String(supplier)
      : Number(supplier);

    const match = portOperatorOptions.find(
      (op) => String(op.value) === String(normalized)
    );

    if (match) {
      setValue('portOperator', match.value as any);
    }
  }, [mode, isPortOperatorUser, supplier, portOperatorOptions, setValue]);

  const showDepartureNoPortsMsg =
    Boolean(watchDepartureCountry) &&
    !isLoadingPorts &&
    departurePorts.length === 0;

  const handleFormSubmit = (values: VesselFormValues) => {
    const submitData = {
      ...values,

      initialETD: values.initialETD
        ? dayjs(values.initialETD).format('YYYY-MM-DDTHH:mm:ss')
        : null,

      actualETD: values.actualETD
        ? dayjs(values.actualETD).format('YYYY-MM-DDTHH:mm:ss')
        : null,

      destinations: values.destinations.map((dest) => ({
        ...dest,

        initialETA: dest.initialETA
          ? dayjs(dest.initialETA).format('YYYY-MM-DDTHH:mm:ss')
          : null,

        actualETA: dest.actualETA
          ? dayjs(dest.actualETA).format('YYYY-MM-DDTHH:mm:ss')
          : null,
      })),
    } as VesselFormValues;

    onSubmit(submitData);
  };

  if (!isRefDataLoaded || (editData && !isReady)) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <RHFForm onSubmit={handleFormSubmit} ref={ref} {...methods}>
      <fieldset
        disabled={readOnly}
        style={{ border: 'none', padding: 0, margin: 0 }}
      >
        <Stack direction='column' spacing={2}>
          {/* Supplier Details */}

          <FormGroup>
            <Stack direction='column' spacing={2}>
              <Typography variant='h5'>Supplier Details</Typography>

              <Stack direction='column' gap={2}>
                <RHFAutocomplete
                  name='imo'
                  options={imoOptions.map((option) => ({
                    label: option.vesselName
                      ? `${option.value} - ${option.vesselName}`
                      : String(option.value),

                    value: option.value,

                    vesselName: option.vesselName ?? '',
                  }))}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?.value === value?.value
                  }
                  getOptionLabel={(option: any) =>
                    typeof option === 'object' && option !== null
                      ? String(option.label || '')
                      : ''
                  }
                  renderOption={(props: any, option: any) => (
                    <li {...props} key={option.value}>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography minWidth={80}>{option.value}</Typography>

                        <Typography>
                          {option.vesselName || 'No Vessel Name'}
                        </Typography>
                      </Stack>
                    </li>
                  )}
                  placeholder='Select IMO Number'
                  label='IMO Number'
                  required
                  sx={
                    topLevelChangedFields?.has('imo')
                      ? scenarioFieldHighlightSx(theme)
                      : undefined
                  }
                />

                <Stack direction='row' gap={2}>
                  <RHFAutocomplete
                    name='shippingCompany'
                    options={shippingCompanyOptions}
                    disabled
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.value === value?.value
                    }
                    getOptionLabel={getIDOptionLabel}
                    renderOption={(props: any, option: any) => (
                      <li {...props} key={option.value}>
                        <IDOptionLabel id={option.value} label={option.label} />
                      </li>
                    )}
                    label='Shipping Company'
                    placeholder='-'
                    textFieldProps={{
                      InputLabelProps: { shrink: true },

                      InputProps: {
                        readOnly: true,

                        endAdornment: null,

                        sx: {
                          border: 'none',
                          '& fieldset': { border: 'none' },
                        },
                      },
                    }}
                  />

                  <RHFTextField
                    label='Vessel Name'
                    name='vessel'
                    placeholder='-'
                    InputLabelProps={{ shrink: true }}
                    readonly
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Stack>
              </Stack>

              {/* Close Supplier Details container Stack */}
            </Stack>
          </FormGroup>

          {/* Voyage */}

          <FormGroup>
            <Stack direction='column' gap={2}>
              <Typography variant='h5'>Voyage</Typography>

              <Stack direction='row' gap={2}>
                <RHFTextField
                  name='voyageNumber'
                  label='Voyage Number'
                  placeholder='Enter Voyage Number'
                  sx={
                    topLevelChangedFields?.has('voyageNumber')
                      ? scenarioFieldHighlightSx(theme)
                      : undefined
                  }
                />

                <RHFAutocomplete
                  name='carrier'
                  options={shippingCompanyOptions}
                  isOptionEqualToValue={(option: any, value: any) =>
                    option?.value === value?.value
                  }
                  getOptionLabel={(option: any) =>
                    typeof option === 'object' && option !== null
                      ? String(option.label || '')
                      : ''
                  }
                  renderOption={(props: any, option: any) => (
                    <li {...props} key={option.value}>
                      <Typography>{option.label}</Typography>
                    </li>
                  )}
                  placeholder='Select Shipping Company'
                  label='Carrier (Optional)'
                  sx={
                    topLevelChangedFields?.has('carrier')
                      ? scenarioFieldHighlightSx(theme)
                      : undefined
                  }
                />
              </Stack>
            </Stack>
          </FormGroup>

          {/* Departure */}

          <FormGroup>
            <Stack direction='column' gap={2}>
              <Typography variant='h5'>Departure</Typography>

              <Stack direction='column' gap={2}>
                <Stack direction='row' gap={2}>
                  <RHFAutocomplete
                    name='departureCountry'
                    options={countryOptions}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.value === value?.value
                    }
                    getOptionLabel={getIDOptionLabel}
                    renderOption={(props: any, option: any) => (
                      <li {...props} key={option.value}>
                        <CountryOptionLabel
                          prefix={option.value}
                          label={option.label}
                          countryCode={option.value}
                        />
                      </li>
                    )}
                    placeholder='Select Departure Country'
                    label='Departure Country'
                    required
                    sx={
                      topLevelChangedFields?.has('departureCountry')
                        ? scenarioFieldHighlightSx(theme)
                        : undefined
                    }
                  />

                  <RHFAutocomplete
                    name='departurePort'
                    options={departurePorts}
                    disabled={!getValues('departureCountry')}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.value === value?.value
                    }
                    placeholder='Select Departure Port'
                    label='Departure Port'
                    required
                    textFieldProps={{
                      helperText: showDepartureNoPortsMsg
                        ? 'No ports available for selected country'
                        : undefined,
                    }}
                    sx={
                      topLevelChangedFields?.has('departurePort')
                        ? scenarioFieldHighlightSx(theme)
                        : undefined
                    }
                  />
                </Stack>

                <Stack direction='row' gap={2}>
                  <RHFAutocomplete
                    name='portOperator'
                    options={portOperatorOptions}
                    disabled={isPortOperatorUser && mode === 'create'}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option?.value === value?.value
                    }
                    getOptionLabel={getIDOptionLabel}
                    renderOption={(props: any, option: any) => (
                      <li {...props} key={option.value}>
                        <Typography>{option.label}</Typography>
                      </li>
                    )}
                    placeholder='Select Port Operator'
                    label='Port Operator'
                    required
                    sx={
                      topLevelChangedFields?.has('portOperator')
                        ? scenarioFieldHighlightSx(theme)
                        : undefined
                    }
                  />

                  <Stack direction='column' gap={1} width='100%'>
                    <Stack
                      direction='row'
                      alignItems='center'
                      width='100%'
                      gap={2}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          ...(topLevelChangedFields?.has('actualETD') &&
                            scenarioFieldHighlightSx(theme)),
                        }}
                      >
                        <RHFDatePicker name='actualETD' label='Latest ETD' />
                      </Box>

                      <FormControl variant='outlined' fullWidth>
                        <FormControlLabel
                          control={
                            <Controller
                              name='slotConfirmed'
                              control={control}
                              render={({ field }) => (
                                <Checkbox
                                  {...field}
                                  checked={!!field.value}
                                  sx={
                                    topLevelChangedFields?.has('slotConfirmed')
                                      ? {
                                          color:
                                            getScenarioHighlightColor(theme),
                                        }
                                      : undefined
                                  }
                                />
                              )}
                            />
                          }
                          label='Slot Confirmed'
                          sx={
                            topLevelChangedFields?.has('slotConfirmed')
                              ? {
                                  color: getScenarioHighlightColor(theme),
                                }
                              : undefined
                          }
                        />
                      </FormControl>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </FormGroup>

          {/* Destinations & Shipments */}

          <FormGroup>
            <Stack direction='column' gap={2}>
              <Typography variant='h5'>
                Destinations ({destinationFields.length})
              </Typography>

              {!!(errors as any)?.destinations?.message && (
                <Typography variant='body2' color='error' sx={{ mt: 0.5 }}>
                  {(errors as any).destinations.message}
                </Typography>
              )}

              {destinationFields.map((field, index) => {
                const isCancelled =
                  watch(`destinations.${index}._cancelled` as any) === true;

                const isExisting = Boolean(
                  watch(`destinations.${index}._voyageId` as any)
                );

                const voyageId = watch(
                  `destinations.${index}._voyageId` as any
                ) as string | undefined;

                const changedFields: Set<string> =
                  (voyageId && voyageChangedFieldsMap?.[voyageId]) ||
                  new Set<string>();

                const isScenarioCancelled =
                  !!voyageId && !!scenarioCancelledVoyageIds?.has(voyageId);

                const isScenarioCreated =
                  watch(`destinations.${index}._scenarioCreated` as any) ===
                  true;

                // Multiple voyages to the same destination port are valid
                // (the backend identifies voyages by UUID, not by port).
                // No port dedup filtering needed.
                const usedPorts: string[] = [];

                return (
                  <Stack
                    key={field.id}
                    direction='row'
                    alignItems='flex-start'
                    spacing={1}
                  >
                    <Box flex={1}>
                      <DestinationBlock
                        destinationIndex={index}
                        countryOptions={countryOptions}
                        marketOptions={marketOptions}
                        mode={mode}
                        onRemove={() => removeDestination(index)}
                        canRemove={destinationFields.length > 1}
                        isCancelled={isCancelled}
                        isScenarioCancelled={isScenarioCancelled}
                        isScenarioCreated={isScenarioCreated}
                        isExisting={isExisting}
                        usedPorts={usedPorts}
                        changedFields={changedFields}
                        shipmentChangedFieldsMap={shipmentChangedFieldsMap}
                        readOnly={readOnly}
                        voyageLabel={
                          mode === 'group-edit' && isExisting
                            ? (watch(
                                `destinations.${index}._voyageNumber` as any
                              ) as string | undefined) || undefined
                            : undefined
                        }
                        onToggleCancel={() =>
                          setValue(
                            `destinations.${index}._cancelled` as any,
                            !isCancelled
                          )
                        }
                      />
                    </Box>
                  </Stack>
                );
              })}

              {!readOnly && (mode === 'create' || mode === 'group-edit') && (
                <Button
                  type='button'
                  onClick={() => appendDestination(defaultDestination as any)}
                  variant='text'
                  startIcon={<Add />}
                  color='primary'
                >
                  Add Destination
                </Button>
              )}
            </Stack>
          </FormGroup>

          {/* Hidden submit for external trigger */}

          <Button
            type='submit'
            style={{ display: 'none' }}
            aria-hidden='true'
          />
        </Stack>
      </fieldset>
    </RHFForm>
  );
});

export default RHFVesselForm;
